/**
 * Admin console › Permit AHJs: the authorities having jurisdiction plan sets use (shipped from
 * app/design/permits/data/ahjs.yaml, plus what staff add): adopted code editions, stamp rules, SolarAPP+, required
 * sheets, fire-setback preset, design wind and snow values, local general notes, submission and the utilities'
 * interconnection forms, each with its sources and the date they were verified. Editing a shipped row keeps it from
 * being refreshed by the next sync.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus } from "lucide-react";
import { useState } from "react";
import { api, errorMessage, unwrap, type S } from "../../api/client";
import { Alert, Badge, Button, Card, Field, Modal, PageHead, Select, Spinner, Switch, TextInput, useToast } from "../../components/ui";
import { type StampRule } from "../../lib/permitAhj";
import { useT, type Translate } from "../../i18n/I18nProvider";
import type { MessageKey } from "../../i18n/keys";
import { useFmt } from "./format";

type Ahj = S["PermitAhj"];
type Level = S["PermitAhjLevel"];

const LEVELS: { value: Level; label: MessageKey }[] = [
  { value: "state", label: "admin.permitAhjs.levelState" },
  { value: "county", label: "admin.permitAhjs.levelCounty" },
  { value: "city", label: "admin.permitAhjs.levelCity" },
];
const STAMPS: { value: string; label: MessageKey }[] = [
  { value: "", label: "admin.permitAhjs.stampNotOnFile" },
  { value: "yes", label: "admin.permitAhjs.stampRequired" },
  { value: "no", label: "admin.permitAhjs.stampNotRequired" },
  { value: "unknown", label: "admin.permitAhjs.stampUnknown" },
];
const PRESETS: { value: string; label: MessageKey }[] = [
  { value: "", label: "admin.permitAhjs.presetNone" },
  { value: "ifc-2021-residential", label: "admin.permitAhjs.presetIfc2021Residential" },
  { value: "ifc-2021-residential-sprinklered", label: "admin.permitAhjs.presetIfc2021Sprinklered" },
  { value: "canada-bc", label: "admin.permitAhjs.presetCanadaBc" },
  { value: "no-setbacks", label: "admin.permitAhjs.presetNoSetbacks" },
];
const SOLARAPP: { value: string; label: MessageKey }[] = [
  { value: "", label: "admin.permitAhjs.solarappUnknown" },
  { value: "yes", label: "admin.permitAhjs.solarappYes" },
  { value: "no", label: "admin.permitAhjs.solarappNo" },
];
const CONFIDENCES: { value: S["PermitAhjConfidence"]; label: MessageKey }[] = [
  { value: "official", label: "admin.permitAhjs.confidenceOfficial" },
  { value: "secondary", label: "admin.permitAhjs.confidenceSecondary" },
  { value: "unverified", label: "admin.permitAhjs.confidenceUnverified" },
];
/** The code abbreviations are names (NEC, IRC…), printed as they are in every language. */
const CODE_NAMES = { nec: "NEC", irc: "IRC", ibc: "IBC", ifc: "IFC", asce: "ASCE" } as const;

/** "NEC 2023 · IRC 2024 · ASCE 7-22 · since 1 Aug 2025", or "Code editions not on file". */
function codeLine(codes: Ahj["codes"] | null | undefined, t: Translate, day: (d: string | null | undefined) => string): string {
  const c = codes ?? {};
  const parts = (Object.keys(CODE_NAMES) as (keyof typeof CODE_NAMES)[]).filter((k) => c[k]).map((k) => `${CODE_NAMES[k]} ${c[k]}`);
  if (!parts.length) return t("admin.permitAhjs.codesNone");
  const line = parts.join(" · ");
  return c.effective ? t("admin.permitAhjs.codesSince", { codes: line, date: day(c.effective) }) : line;
}

interface Draft {
  id?: string;
  level: Level;
  state: string;
  county: string;
  city: string;
  name: string;
  aliases: string;
  nec: string;
  irc: string;
  ibc: string;
  ifc: string;
  asce: string;
  effective: string;
  codeNotes: string;
  stampStructural: string;
  stampElectrical: string;
  solarapp: string;
  requiredSheets: string;
  setbackPreset: string;
  fireNotes: string;
  windMph: string;
  snowPsf: string;
  designSource: string;
  exposureHint: string;
  generalNotes: string;
  submissionMethod: string;
  submissionUrl: string;
  utilities: string;
  sources: string;
  verifiedOn: string;
  confidence: S["PermitAhjConfidence"];
  lat: string;
  lng: string;
  radius: string;
  active: boolean;
}

const str = (v: unknown) => (v == null ? "" : String(v));
const lines = (s: string) => s.split("\n").map((l) => l.trim()).filter(Boolean);
const orNull = (s: string) => (s.trim() ? s.trim() : null);
const numOrNull = (s: string) => (s.trim() && !Number.isNaN(Number(s)) ? Number(s) : null);

function toDraft(a?: Ahj): Draft {
  const c = a?.codes ?? {};
  return {
    id: a?.id,
    level: a?.level ?? "city",
    state: a?.state ?? "",
    county: str(a?.county),
    city: str(a?.city),
    name: a?.name ?? "",
    aliases: (a?.aliases ?? []).join(", "),
    nec: str(c.nec),
    irc: str(c.irc),
    ibc: str(c.ibc),
    ifc: str(c.ifc),
    asce: str(c.asce),
    effective: str(c.effective),
    codeNotes: str(c.notes),
    stampStructural: str(a?.stamp_structural),
    stampElectrical: str(a?.stamp_electrical),
    solarapp: a?.solarapp == null ? "" : a.solarapp ? "yes" : "no",
    requiredSheets: (a?.required_sheets ?? []).join("\n"),
    setbackPreset: str(a?.setback_preset),
    fireNotes: str(a?.fire_notes),
    windMph: str(a?.design?.wind_mph),
    snowPsf: str(a?.design?.ground_snow_psf),
    designSource: str(a?.design?.source),
    exposureHint: str(a?.exposure_hint),
    generalNotes: str(a?.general_notes),
    submissionMethod: str(a?.submission?.method),
    submissionUrl: str(a?.submission?.url),
    utilities: (a?.utilities ?? []).map((u) => [u.name, u.form_url ?? "", u.notes ?? ""].join(" | ").replace(/( \| )+$/, "")).join("\n"),
    sources: (a?.sources ?? []).map((s) => (s.url ? `${s.title} | ${s.url}` : s.title)).join("\n"),
    verifiedOn: str(a?.verified_on) || new Date().toISOString().slice(0, 10),
    confidence: a?.confidence ?? "official",
    lat: str(a?.center_lat),
    lng: str(a?.center_lng),
    radius: str(a?.radius_km),
    active: a?.active ?? true,
  };
}

function toBody(d: Draft): S["PermitAhjUpdate"] {
  const wind = numOrNull(d.windMph);
  const snow = numOrNull(d.snowPsf);
  return {
    level: d.level,
    state: d.state.trim().toUpperCase(),
    county: orNull(d.county),
    city: orNull(d.city),
    name: d.name.trim(),
    aliases: d.aliases.split(",").map((s) => s.trim()).filter(Boolean),
    codes: { nec: orNull(d.nec), irc: orNull(d.irc), ibc: orNull(d.ibc), ifc: orNull(d.ifc), asce: orNull(d.asce), effective: orNull(d.effective), notes: orNull(d.codeNotes) },
    stamp_structural: (orNull(d.stampStructural) as StampRule | null) ?? null,
    stamp_electrical: (orNull(d.stampElectrical) as StampRule | null) ?? null,
    solarapp: d.solarapp === "" ? null : d.solarapp === "yes",
    required_sheets: lines(d.requiredSheets).length ? lines(d.requiredSheets) : null,
    setback_preset: orNull(d.setbackPreset),
    fire_notes: orNull(d.fireNotes),
    design: wind != null || snow != null ? { wind_mph: wind, ground_snow_psf: snow, source: orNull(d.designSource) } : null,
    exposure_hint: orNull(d.exposureHint),
    general_notes: orNull(d.generalNotes),
    submission: d.submissionMethod.trim() || d.submissionUrl.trim() ? { method: orNull(d.submissionMethod), url: orNull(d.submissionUrl) } : null,
    utilities: lines(d.utilities).map((l) => {
      const [name, form_url, notes] = l.split("|").map((s) => s.trim());
      return { name: name!, form_url: form_url || null, notes: notes || null };
    }),
    sources: lines(d.sources).map((l) => {
      const [title, url] = l.split("|").map((s) => s.trim());
      return { title: title!, url: url || null };
    }),
    verified_on: orNull(d.verifiedOn),
    confidence: d.confidence,
    center_lat: numOrNull(d.lat),
    center_lng: numOrNull(d.lng),
    radius_km: numOrNull(d.radius),
    active: d.active,
  };
}

export function AdminPermitAhjs() {
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const { date } = useFmt();
  /** A calendar day ("2025-08-01") read as local midnight, so it never shows as the day before. */
  const day = (d: string | null | undefined) => (d ? date(`${d.slice(0, 10)}T00:00:00`) : date(null));
  const [q, setQ] = useState("");
  const [draft, setDraft] = useState<Draft | null>(null);
  const list = useQuery({
    queryKey: ["admin-permit-ahjs"],
    queryFn: () => unwrap(api.GET("/v1/permit-ahjs", { params: { query: { include_inactive: true } } })),
  });
  const save = useMutation({
    mutationFn: async (d: Draft) => {
      const body = toBody(d);
      return d.id
        ? unwrap(api.PATCH("/v1/admin/permit-ahjs/{ahj_id}", { params: { path: { ahj_id: d.id } }, body }))
        : unwrap(api.POST("/v1/admin/permit-ahjs", { body: body as S["PermitAhjWrite"] }));
    },
    onSuccess: (a) => {
      qc.invalidateQueries({ queryKey: ["admin-permit-ahjs"] });
      qc.invalidateQueries({ queryKey: ["permit-ahjs"] });
      setDraft(null);
      toast(t("admin.permitAhjs.saved", { name: a.name ?? "" }));
    },
  });
  const needle = q.trim().toLowerCase();
  const items = (list.data?.items ?? []).filter((a) => !needle || [a.name, a.city, a.county, a.state, a.key].some((v) => v?.toLowerCase().includes(needle)));
  const set = (patch: Partial<Draft>) => draft && setDraft({ ...draft, ...patch });
  const options = <T extends string>(list: { value: T; label: MessageKey }[]) => list.map((o) => ({ value: o.value, label: t(o.label) }));
  const stamp = (rule: string | null | undefined) => (rule === "yes" || rule === "no" ? rule : "unknown") as StampRule;
  return (
    <>
      <PageHead
        title={t("admin.permitAhjs.title")}
        description={t("admin.permitAhjs.description")}
        actions={
          <Button variant="primary" icon={<Plus />} onClick={() => setDraft(toDraft())}>
            {t("admin.permitAhjs.add")}
          </Button>
        }
      />
      <Card
        pad={false}
        title={t("admin.permitAhjs.count", { count: items.length })}
        actions={
          <div style={{ width: 240 }}>
            <TextInput value={q} placeholder={t("admin.permitAhjs.searchPlaceholder")} onChange={(e) => setQ(e.target.value)} aria-label={t("admin.common.search")} />
          </div>
        }
      >
        {list.isLoading ? (
          <Spinner />
        ) : list.error ? (
          <Alert tone="danger">{errorMessage(list.error)}</Alert>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("admin.permitAhjs.colAhj")}</th>
                  <th>{t("admin.permitAhjs.colCodes")}</th>
                  <th>{t("admin.permitAhjs.colStamps")}</th>
                  <th>{t("admin.permitAhjs.colSolarapp")}</th>
                  <th>{t("admin.permitAhjs.colVerified")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <b dir="auto">{a.name}</b>
                      <div className="small muted">
                        {a.design?.wind_mph
                          ? t("admin.permitAhjs.rowMetaWind", { state: a.state ?? "", level: a.level ?? "", wind: a.design.wind_mph })
                          : t("admin.permitAhjs.rowMeta", { state: a.state ?? "", level: a.level ?? "" })}
                      </div>
                    </td>
                    <td className="small" dir="auto">{codeLine(a.codes, t, day)}</td>
                    <td className="small">
                      {t("admin.permitAhjs.stampStructuralShort", { rule: stamp(a.stamp_structural) })}
                      <br />
                      {t("admin.permitAhjs.stampElectricalShort", { rule: stamp(a.stamp_electrical) })}
                    </td>
                    <td>{a.solarapp == null ? <span className="muted">—</span> : a.solarapp ? <Badge tone="ok">{t("admin.common.yes")}</Badge> : t("admin.common.no")}</td>
                    <td className="small">
                      <div className="row" style={{ gap: 4, flexWrap: "wrap" }}>
                        <Badge tone={a.confidence === "official" ? "ok" : "warn"}>{t("admin.permitAhjs.confidenceBadge", { confidence: a.confidence ?? "" })}</Badge>
                        {!a.active && <Badge>{t("admin.permitAhjs.hidden")}</Badge>}
                        {a.source === "shipped" && <Badge tone="info">{t("admin.permitAhjs.shipped")}</Badge>}
                      </div>
                      <span className="muted">{day(a.verified_on)}</span>
                    </td>
                    <td>
                      <Button size="sm" variant="ghost" icon={<Pencil size={14} />} onClick={() => setDraft(toDraft(a))}>
                        {t("admin.common.edit")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {draft && (
        <Modal
          title={draft.id ? t("admin.permitAhjs.editTitle", { name: draft.name }) : t("admin.permitAhjs.add")}
          onClose={() => setDraft(null)}
          width={820}
          footer={
            <>
              <Button onClick={() => setDraft(null)}>{t("admin.common.cancel")}</Button>
              <Button variant="primary" loading={save.isPending} disabled={!draft.name.trim() || draft.state.trim().length !== 2} onClick={() => save.mutate(draft)}>
                {t("admin.common.save")}
              </Button>
            </>
          }
        >
          <div className="stack">
            {save.error && <Alert tone="danger">{errorMessage(save.error)}</Alert>}
            <div className="form-grid">
              <Field label={t("admin.permitAhjs.fieldName")}>
                <TextInput dir="auto" value={draft.name} onChange={(e) => set({ name: e.target.value })} />
              </Field>
              <Field label={t("admin.permitAhjs.fieldLevel")}>
                <Select<Level> value={draft.level} onChange={(v) => set({ level: v })} options={options(LEVELS)} />
              </Field>
              <Field label={t("admin.permitAhjs.fieldState")} hint={t("admin.permitAhjs.fieldStateHint")}>
                <TextInput dir="ltr" value={draft.state} maxLength={2} onChange={(e) => set({ state: e.target.value.toUpperCase() })} />
              </Field>
              <Field label={t("admin.permitAhjs.fieldCounty")}>
                <TextInput dir="auto" value={draft.county} onChange={(e) => set({ county: e.target.value })} />
              </Field>
              <Field label={t("admin.permitAhjs.fieldCity")}>
                <TextInput dir="auto" value={draft.city} onChange={(e) => set({ city: e.target.value })} />
              </Field>
              <Field label={t("admin.permitAhjs.fieldAliases")} hint={t("admin.permitAhjs.fieldAliasesHint")}>
                <TextInput dir="auto" value={draft.aliases} onChange={(e) => set({ aliases: e.target.value })} />
              </Field>
            </div>
            <h4>{t("admin.permitAhjs.sectionCodes")}</h4>
            <div className="form-grid">
              {(["nec", "irc", "ibc", "ifc", "asce"] as const).map((k) => (
                <Field
                  key={k}
                  label={k === "asce" ? t("admin.permitAhjs.fieldAsce") : CODE_NAMES[k]}
                  hint={k === "asce" ? t("admin.permitAhjs.fieldAsceHint") : t("admin.permitAhjs.fieldEditionHint")}
                >
                  <TextInput dir="ltr" value={draft[k]} onChange={(e) => set({ [k]: e.target.value } as Partial<Draft>)} />
                </Field>
              ))}
              <Field label={t("admin.permitAhjs.fieldEffective")}>
                <TextInput type="date" value={draft.effective} onChange={(e) => set({ effective: e.target.value })} />
              </Field>
            </div>
            <Field label={t("admin.permitAhjs.fieldCodeNotes")}>
              <TextInput dir="auto" value={draft.codeNotes} onChange={(e) => set({ codeNotes: e.target.value })} />
            </Field>
            <h4>{t("admin.permitAhjs.sectionRequirements")}</h4>
            <div className="form-grid">
              <Field label={t("admin.permitAhjs.fieldStampStructural")}>
                <Select value={draft.stampStructural} onChange={(v) => set({ stampStructural: v })} options={options(STAMPS)} />
              </Field>
              <Field label={t("admin.permitAhjs.fieldStampElectrical")}>
                <Select value={draft.stampElectrical} onChange={(v) => set({ stampElectrical: v })} options={options(STAMPS)} />
              </Field>
              <Field label={t("admin.permitAhjs.colSolarapp")}>
                <Select value={draft.solarapp} onChange={(v) => set({ solarapp: v })} options={options(SOLARAPP)} />
              </Field>
              <Field label={t("admin.permitAhjs.fieldFireSetbacks")}>
                <Select value={draft.setbackPreset} onChange={(v) => set({ setbackPreset: v })} options={options(PRESETS)} />
              </Field>
            </div>
            <Field label={t("admin.permitAhjs.fieldRequiredSheets")} hint={t("admin.permitAhjs.fieldRequiredSheetsHint")}>
              <textarea className="textarea" dir="auto" rows={3} value={draft.requiredSheets} onChange={(e) => set({ requiredSheets: e.target.value })} />
            </Field>
            <Field label={t("admin.permitAhjs.fieldFireNotes")}>
              <TextInput dir="auto" value={draft.fireNotes} onChange={(e) => set({ fireNotes: e.target.value })} />
            </Field>
            <Field label={t("admin.permitAhjs.fieldGeneralNotes")} hint={t("admin.permitAhjs.fieldGeneralNotesHint")}>
              <textarea className="textarea" dir="auto" rows={3} value={draft.generalNotes} onChange={(e) => set({ generalNotes: e.target.value })} />
            </Field>
            <h4>{t("admin.permitAhjs.sectionDesign")}</h4>
            <div className="form-grid">
              <Field label={t("admin.permitAhjs.fieldWind")} hint={t("admin.permitAhjs.fieldWindHint")}>
                <TextInput dir="ltr" value={draft.windMph} inputMode="decimal" onChange={(e) => set({ windMph: e.target.value })} />
              </Field>
              <Field label={t("admin.permitAhjs.fieldSnow")} hint={t("admin.permitAhjs.fieldSnowHint")}>
                <TextInput dir="ltr" value={draft.snowPsf} inputMode="decimal" onChange={(e) => set({ snowPsf: e.target.value })} />
              </Field>
              <Field label={t("admin.permitAhjs.fieldDesignSource")}>
                <TextInput dir="auto" value={draft.designSource} onChange={(e) => set({ designSource: e.target.value })} />
              </Field>
              <Field label={t("admin.permitAhjs.fieldExposure")} hint={t("admin.permitAhjs.fieldExposureHint")}>
                <TextInput dir="auto" value={draft.exposureHint} onChange={(e) => set({ exposureHint: e.target.value })} />
              </Field>
            </div>
            <h4>{t("admin.permitAhjs.sectionSubmission")}</h4>
            <div className="form-grid">
              <Field label={t("admin.permitAhjs.fieldSubmissionMethod")}>
                <TextInput dir="auto" value={draft.submissionMethod} onChange={(e) => set({ submissionMethod: e.target.value })} />
              </Field>
              <Field label={t("admin.permitAhjs.fieldSubmissionUrl")}>
                <TextInput dir="ltr" value={draft.submissionUrl} onChange={(e) => set({ submissionUrl: e.target.value })} />
              </Field>
            </div>
            <Field label={t("admin.permitAhjs.fieldUtilities")} hint={t("admin.permitAhjs.fieldUtilitiesHint")}>
              <textarea className="textarea" dir="auto" rows={2} value={draft.utilities} onChange={(e) => set({ utilities: e.target.value })} />
            </Field>
            <h4>{t("admin.permitAhjs.sectionSources")}</h4>
            <Field label={t("admin.permitAhjs.fieldSources")} hint={t("admin.permitAhjs.fieldSourcesHint")}>
              <textarea className="textarea" dir="auto" rows={3} value={draft.sources} onChange={(e) => set({ sources: e.target.value })} />
            </Field>
            <div className="form-grid">
              <Field label={t("admin.permitAhjs.fieldVerifiedOn")}>
                <TextInput type="date" value={draft.verifiedOn} onChange={(e) => set({ verifiedOn: e.target.value })} />
              </Field>
              <Field label={t("admin.permitAhjs.fieldConfidence")}>
                <Select<S["PermitAhjConfidence"]> value={draft.confidence} onChange={(v) => set({ confidence: v })} options={options(CONFIDENCES)} />
              </Field>
              <Field label={t("admin.permitAhjs.fieldLat")}>
                <TextInput dir="ltr" value={draft.lat} inputMode="decimal" onChange={(e) => set({ lat: e.target.value })} />
              </Field>
              <Field label={t("admin.permitAhjs.fieldLng")}>
                <TextInput dir="ltr" value={draft.lng} inputMode="decimal" onChange={(e) => set({ lng: e.target.value })} />
              </Field>
              <Field label={t("admin.permitAhjs.fieldRadius")} hint={t("admin.permitAhjs.fieldRadiusHint")}>
                <TextInput dir="ltr" value={draft.radius} inputMode="decimal" onChange={(e) => set({ radius: e.target.value })} />
              </Field>
            </div>
            <Switch checked={draft.active} onChange={(v) => set({ active: v })} label={t("admin.permitAhjs.fieldActive")} />
          </div>
        </Modal>
      )}
    </>
  );
}
